import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  Phone,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Clock,
  RefreshCw,
  Star,
  Home,
  ShieldCheck,
  Building2,
} from "lucide-react";
import Notification, { showToast } from "../../components/common/notification/Notification";
import { loginSuccess } from "../../features/auth/authSlice";
import { getStoredAuthSession } from "./authSession";
import Logo from "../../assets/images/logo.png";
import DefaultHeroImg from "../../assets/images/hero.jpg";
import "./AuthForm.css";

const BASE_URL = (
  process.env.REACT_APP_BASE_URL || "https://services.allhomedesire.in"
).replace(/\/+$/, "");

const WhatsAppIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"
      fill="#25D366"
    />
    <path
      d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.532 5.857L0 24l6.306-1.508A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.797 9.797 0 01-5.003-1.373l-.36-.214-3.737.893.938-3.63-.235-.374A9.772 9.772 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z"
      fill="#25D366"
    />
  </svg>
);

export const AuthForm = ({
  type = "login",
  title = "Welcome",
  subtitle = "Login with your mobile number to continue",
  image = DefaultHeroImg,
  redirectOnSuccess = "/customer/myspace",
  footerText = "Don’t have an account?",
  footerLink = "Sign Up",
  footerPath = "/sign-up",
  userType = "CUSTOMER",
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(120);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isValidatingOtp, setIsValidatingOtp] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRefs = useRef([...Array(6)].map(() => React.createRef()));

  // Auto-redirect if already logged in with valid token
  useEffect(() => {
    const savedAuth = getStoredAuthSession();
    if (savedAuth?.accessToken) {
      dispatch(loginSuccess(savedAuth));
      navigate("/dashboard/myspace", { replace: true });
    }
  }, [dispatch, navigate]);

  // Countdown timer for OTP resend
  useEffect(() => {
    let interval = null;
    if (otpSent && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) setPhoneNumber(value);
  };

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    if (phoneNumber.length !== 10) {
      showToast("Please enter a valid 10-digit mobile number.", { type: "error" });
      return;
    }

    setIsSendingOtp(true);
    try {
      const formattedMobile = "91" + phoneNumber;
      const response = await axios.post(`${BASE_URL}/all/otp/request`, {
        mobile: formattedMobile,
        action: type,
      });

      if (response?.data?.statusType === "SUCCESS" || response?.data?.status === "SUCCESS") {
        setOtpSent(true);
        setTimer(120);
        setOtp(["", "", "", "", "", ""]);
        showToast("OTP sent successfully to your mobile number!", { type: "success" });
        setTimeout(() => inputRefs.current[0]?.current?.focus(), 150);
      } else {
        const errorMsg = response?.data?.text || response?.data?.message || "Failed to send OTP.";
        showToast(errorMsg, { type: "error" });
      }
    } catch (error) {
      console.error("OTP send error:", error);
      const msg = error?.response?.data?.text || error?.response?.data?.message || "An error occurred while sending OTP.";
      showToast(msg, { type: "error" });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleOtpChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, "");
    if (!val) return;

    const newOtp = [...otp];
    newOtp[idx] = val.slice(-1);
    setOtp(newOtp);

    if (idx < 5 && val) {
      inputRefs.current[idx + 1].current?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[idx]) {
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) {
        inputRefs.current[idx - 1].current?.focus();
        newOtp[idx - 1] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      inputRefs.current[idx - 1].current?.focus();
    } else if (e.key === "ArrowRight" && idx < 5) {
      inputRefs.current[idx + 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasteData) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pasteData[i] || "";
      }
      setOtp(newOtp);
      const focusIndex = Math.min(pasteData.length, 5);
      inputRefs.current[focusIndex].current?.focus();
    }
  };

  const handleValidateOtp = async (e) => {
    if (e) e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      showToast("Please enter a valid 6-digit OTP code.", { type: "error" });
      return;
    }

    setIsValidatingOtp(true);
    try {
      const formattedMobile = "91" + phoneNumber;
      const response = await axios.post(`${BASE_URL}/all/otp/verify`, {
        mobile: formattedMobile,
        otp: otpValue,
        user_type: userType,
      });

      if (response?.data?.statusType === "SUCCESS" || response?.data?.status === "SUCCESS") {
        showToast("Mobile verified successfully!", { type: "success" });

        const sessionPayload = {
          statusType: response.data.statusType,
          accessToken: response.data.accessToken || response.data.token,
          token: response.data.token || response.data.accessToken,
          refreshToken: response.data.refreshToken,
          user: response.data.user,
        };

        dispatch(loginSuccess(sessionPayload));
        navigate(redirectOnSuccess, { replace: true });
        window.location.reload();
      } else {
        const errorMsg = response?.data?.text || response?.data?.message || "Invalid or expired OTP code.";
        showToast(errorMsg, { type: "error" });
      }
    } catch (error) {
      console.error("OTP validation error:", error);
      const msg = error?.response?.data?.text || error?.response?.data?.message || "An error occurred during OTP validation.";
      showToast(msg, { type: "error" });
    } finally {
      setIsValidatingOtp(false);
    }
  };

  const formatTimer = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const bgImage = image || DefaultHeroImg;

  return (
    <div className="auth-split-layout">
      <Notification />

      {/* LEFT VISUAL PANEL (50%) */}
      <div className="auth-split__left">
        <div
          className="auth-split__left-bg"
          style={{ backgroundImage: `url(${bgImage})` }}
        />
        <div className="auth-split__left-overlay" />

        <div className="auth-split__left-content">
          <div className="auth-visual-card">
            {/* Top Glass Trust Badge */}
            <div className="auth-visual-card__badge">
              <span className="auth-visual-card__badge-dot" />
              <span>10k+ Verified Properties</span>
            </div>

            <div className="auth-visual-card__brand">ALL HOME DESIRE</div>

            <h2 className="auth-visual-card__title">
              Find Your <span className="auth-visual-card__highlight">Perfect Home</span> Today
            </h2>

            <p className="auth-visual-card__subtitle">
              "Thousands of verified properties across India — transparent, effortless, and tailored to you."
            </p>

            <div className="auth-visual-card__highlights">
              <div className="auth-visual-highlight">
                <div className="auth-visual-highlight__icon">
                  <Home size={18} />
                </div>
                <div className="auth-visual-highlight__text">
                  <h4>100% Verified Listings</h4>
                  <p>Directly verified flats, villas, plots & commercial spaces</p>
                </div>
              </div>

              <div className="auth-visual-highlight">
                <div className="auth-visual-highlight__icon">
                  <ShieldCheck size={18} />
                </div>
                <div className="auth-visual-highlight__text">
                  <h4>Zero Brokerage & Direct Connect</h4>
                  <p>Transparent property deals with verified owners and builders</p>
                </div>
              </div>

              <div className="auth-visual-highlight">
                <div className="auth-visual-highlight__icon">
                  <Building2 size={18} />
                </div>
                <div className="auth-visual-highlight__text">
                  <h4>Complete Home Ecosystem</h4>
                  <p>Access interior designers, contractors, and local home services</p>
                </div>
              </div>
            </div>

            {/* Social proof rating */}
            <div className="auth-visual-social">
              <div className="auth-visual-social__stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#f59e0b" color="#f59e0b" />
                ))}
                <span className="auth-visual-social__score">4.9 / 5</span>
              </div>
              <p className="auth-visual-social__text">
                Trusted by 100,000+ happy homeowners & property seekers across India
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT FORM PANEL (50%) */}
      <div className="auth-split__right">
        <div className="auth-split__right-content">
          {/* Header Branding */}
          <div className="auth-split__header">
            <Link to="/" className="auth-split__logo-link">
              <img src={Logo} alt="All Home Desire" className="auth-split__logo" />
            </Link>
            <span className="auth-split__badge">
              {type === "register" ? "NEW ACCOUNT" : "CUSTOMER PORTAL"}
            </span>
          </div>

          {/* STATE 1: MOBILE NUMBER ENTRY */}
          {!otpSent ? (
            <div className="auth-step-pane">
              <div className="auth-split__titles">
                <h1 className="auth-split__title">{title}</h1>
                <p className="auth-split__subtitle">{subtitle}</p>
              </div>

              <form onSubmit={handleSendOtp} className="auth-form-body">
                <div className="auth-form__group">
                  <label className="auth-form__label" htmlFor="customer-mobile">
                    Mobile Number <span className="auth-required">*</span>
                  </label>
                  <div className="auth-input-wrap">
                    <span className="auth-input-icon">
                      <Phone size={18} />
                    </span>
                    <span className="auth-input-divider" />
                    <span className="auth-input-prefix">+91</span>
                    <input
                      id="customer-mobile"
                      type="tel"
                      inputMode="numeric"
                      placeholder="Enter 10 digit number"
                      className="auth-input"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      maxLength={10}
                      autoFocus
                    />
                  </div>
                  <div className="auth-whatsapp-note">
                    <WhatsAppIcon />
                    <span>OTP will be sent to your WhatsApp number</span>
                  </div>
                </div>

                <button
                  type="submit"
                  className="auth-btn auth-btn--primary auth-btn--lg"
                  disabled={phoneNumber.length !== 10 || isSendingOtp}
                >
                  {isSendingOtp ? (
                    <span className="auth-spinner">Sending OTP...</span>
                  ) : (
                    <>
                      <span>Send OTP</span>
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                {footerText && footerLink && (
                  <div className="auth-switch">
                    <span>{footerText}</span>
                    <Link to={footerPath} className="auth-switch__link">
                      {footerLink}
                    </Link>
                  </div>
                )}
              </form>
            </div>
          ) : (
            /* STATE 2: 6-DIGIT OTP VERIFICATION */
            <div className="auth-step-pane">
              <div className="auth-split__titles">
                <h1 className="auth-split__title">Verify your number</h1>
                <p className="auth-split__subtitle">
                  We've sent a 6-digit verification code to +91 {phoneNumber}
                </p>
              </div>

              <div className="auth-verify-target-row">
                <div className="auth-verify-target-badge">
                  <span>+91 {phoneNumber}</span>
                </div>
                <button
                  type="button"
                  className="auth-verify-change-btn"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp(["", "", "", "", "", ""]);
                  }}
                >
                  Change number
                </button>
              </div>

              <form onSubmit={handleValidateOtp} className="auth-form-body">
                <div className="auth-form__group text-center">
                  <label className="auth-form__label text-center mb-2">
                    Enter 6-digit OTP code
                  </label>
                  <div className="auth-otp-grid" onPaste={handlePaste}>
                    {otp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={inputRefs.current[idx]}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        className={`auth-otp-input ${digit ? "auth-otp-input--filled" : ""}`}
                        onChange={(e) => handleOtpChange(e, idx)}
                        onKeyDown={(e) => handleKeyDown(e, idx)}
                        autoFocus={idx === 0}
                      />
                    ))}
                  </div>
                </div>

                <div className="auth-timer-section">
                  {timer > 0 ? (
                    <div className="auth-timer-badge">
                      <Clock size={15} />
                      <span>Code expires in {formatTimer(timer)}</span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="auth-resend-btn"
                      onClick={handleSendOtp}
                      disabled={isSendingOtp}
                    >
                      <RefreshCw size={14} className={isSendingOtp ? "auth-spin" : ""} />
                      <span>{isSendingOtp ? "Resending code..." : "Resend OTP Code"}</span>
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  className="auth-btn auth-btn--primary auth-btn--lg"
                  disabled={otp.join("").length !== 6 || isValidatingOtp}
                >
                  {isValidatingOtp ? (
                    <span className="auth-spinner">Validating OTP...</span>
                  ) : (
                    <>
                      <span>Verify & Continue</span>
                      <CheckCircle2 size={18} />
                    </>
                  )}
                </button>

                <div className="auth-switch">
                  <button
                    type="button"
                    className="auth-back-link"
                    onClick={() => {
                      setOtpSent(false);
                      setOtp(["", "", "", "", "", ""]);
                    }}
                  >
                    <ArrowLeft size={16} />
                    <span>Back to mobile entry</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Footer note */}
          <div className="auth-split__footer">
            <span>© {new Date().getFullYear()} All Home Desire. All rights reserved.</span>
            <div className="auth-split__footer-links">
              <Link to="/terms-conditions">Terms</Link>
              <span>•</span>
              <Link to="/privacy-policy">Privacy</Link>
              <span>•</span>
              <Link to="/contact">Support</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
